/**
 * announcementService.ts — HTTP client for /announcements endpoints.
 *
 * Vendor flow: list mine, create, publish, withdraw.
 * Distributor flow: feed, convert-to-handshake.
 */
import api from "./api";
import type { Handshake } from "./handshakeService";

export type AnnouncementKind = "availability" | "restock";
export type AnnouncementStatus =
  | "draft"
  | "published"
  | "expired"
  | "withdrawn";

export interface Announcement {
  _id: string;
  companyId: string;
  businessId: string;
  kind: AnnouncementKind;
  title: string;
  body?: string | null;
  productId?: string | null;
  productName?: string | null;
  unitPriceMinor?: number | null;
  currency: string;
  availableQuantity?: number | null;
  status: AnnouncementStatus;
  publishedAt?: string | null;
  publishedByUserId?: string | null;
  expiresAt?: string | null;
  withdrawnAt?: string | null;
  withdrawnByUserId?: string | null;
  withdrawnReason?: string | null;
  autoGenerated: boolean;
  triggerSourceKind?: string | null;
  triggerSourceId?: string | null;
  conversionCount: number;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAnnouncementInput {
  kind: AnnouncementKind;
  title: string;
  body?: string;
  productId?: string;
  productName?: string;
  unitPriceMinor?: number;
  currency?: string;
  availableQuantity?: number;
}

export interface ConvertToHandshakeInput {
  quantity: number;
  note?: string;
  expectedDeliveryAt?: string;
}

export const announcementService = {
  listMine: async (status?: AnnouncementStatus): Promise<Announcement[]> => {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    const res = await api.get<{ announcements: Announcement[] }>(
      "/announcements/mine",
      { params },
    );
    return res.data.announcements;
  },

  feed: async (kind?: AnnouncementKind): Promise<Announcement[]> => {
    const params: Record<string, string> = {};
    if (kind) params.kind = kind;
    const res = await api.get<{ announcements: Announcement[] }>(
      "/announcements/feed",
      { params },
    );
    return res.data.announcements;
  },

  get: async (id: string): Promise<Announcement> => {
    const res = await api.get<{ announcement: Announcement }>(
      `/announcements/${id}`,
    );
    return res.data.announcement;
  },

  create: async (input: CreateAnnouncementInput): Promise<Announcement> => {
    const res = await api.post<{ announcement: Announcement }>(
      "/announcements",
      input,
    );
    return res.data.announcement;
  },

  publish: async (id: string, expiresAt?: string): Promise<Announcement> => {
    const res = await api.post<{ announcement: Announcement }>(
      `/announcements/${id}/publish`,
      { expiresAt },
    );
    return res.data.announcement;
  },

  withdraw: async (id: string, reason?: string): Promise<Announcement> => {
    const res = await api.post<{ announcement: Announcement }>(
      `/announcements/${id}/withdraw`,
      { reason },
    );
    return res.data.announcement;
  },

  convertToHandshake: async (
    id: string,
    input: ConvertToHandshakeInput,
  ): Promise<Handshake> => {
    const res = await api.post<{ handshake: Handshake }>(
      `/announcements/${id}/convert`,
      input,
    );
    return res.data.handshake;
  },
};

export default announcementService;
